import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LoaderCircle } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { CopyButton } from '@/_shared/components/CopyButton';
import { Badge } from '@/_shared/components/ui/badge';
import { Button } from '@/_shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/_shared/components/ui/dialog';
import { Input } from '@/_shared/components/ui/input';
import { Label } from '@/_shared/components/ui/label';
import { Switch } from '@/_shared/components/ui/switch';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';
import { useIsSiblingWorkspace } from '@/_shared/hooks/useSiblingStatus';

import { ReadableWorkspacesSection } from '../components/readableWorkspaces/ReadableWorkspacesSection';
import { usePublicSharingMutation } from '../hooks/useSettingsSwitchMutations';
import { PUBLIC_SHARING } from '../lib/settingsSwitches';
import { SettingsMembers } from './SettingsMembers';

export function Settings(): React.ReactElement {
  usePageTitle('Settings');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { slug, isOwner, isAdmin, workspace } = useWorkspace();
  const orpc = useOrpc();
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { data, isLoading } = useQuery(orpc.settings.get.queryOptions({ input: {} }));
  // ACT2-1124: the model, not the organization id, which a member does not get. Null while it is read.
  const isSiblingWorkspace = useIsSiblingWorkspace();
  // ACT2-1124: a sibling workspace names its RudderStack organization; a shared-key workspace its linked workspace.
  const organizationId = data?.settings?.rudderstackOrganizationId ?? null;
  const workspaceId = data?.settings?.rudderstackWorkspaceId ?? null;
  let connectedId: { label: string; value: string } | null = null;
  if (organizationId) connectedId = { label: 'Connected RudderStack Organization ID', value: organizationId };
  else if (workspaceId) connectedId = { label: 'Connected RudderStack Workspace ID', value: workspaceId };

  const publicSharingMutation = usePublicSharingMutation();

  const deleteMutation = useMutation(
    orpc.workspace.delete.mutationOptions({
      onSuccess: () => {
        toast.success('Workspace deleted');
        // Cancel in-flight queries first to prevent 404 toasts from stale workspace queries
        void queryClient.cancelQueries();
        void queryClient.invalidateQueries();
        void navigate('/');
      },
      onError: (err: Error) => {
        // A refused delete says why: only an RS admin can delete a sibling workspace (ACT2-1124).
        const code = (err as Error & { code?: string }).code;
        if (code === 'FORBIDDEN' && err.message) toast.error(err.message);
        else if (err.message.includes('rudderstack_unavailable')) toast.error('RudderStack did not answer. Try again.');
        else toast.error('Failed to delete workspace');
      },
    }),
  );

  const handleDelete = useCallback(() => {
    if (deleteConfirm !== workspace.id) return;
    deleteMutation.mutate({});
  }, [deleteConfirm, workspace.id, deleteMutation]);

  const handleLeave = useCallback(async () => {
    if (!slug) return;
    try {
      const res = await fetch('/api/workspace/leave', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ workspaceId: slug }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? 'Failed to leave workspace');
      }
      toast.success('Left workspace');
      // Cancel in-flight queries first so stale left-workspace queries don't 403 before we navigate away.
      void queryClient.cancelQueries();
      void queryClient.invalidateQueries();
      void navigate('/');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to leave workspace');
    }
  }, [queryClient, slug, navigate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <LoaderCircle size={24} className="animate-spin" />
      </div>
    );
  }

  return (
    <>
      {/* Public sharing — members see it disabled with an explanatory tooltip. */}
      {data?.settings && (
        <section className="mb-8">
          <h2 className="mb-4 text-sm font-bold tracking-wider text-muted-foreground uppercase">Public sharing</h2>
          <div className="flex items-start justify-between gap-4 overflow-hidden rounded-lg border border-border px-4 py-4">
            <div>
              <label id="public-sharing-label" className="block text-sm font-medium text-grey-800">
                {PUBLIC_SHARING.title}
              </label>
              <p className="mt-1.5 text-sm text-grey-500">{PUBLIC_SHARING.description}</p>
            </div>
            <ActionTooltip show={!isAdmin} message="Only workspace admins can change public sharing">
              <Switch
                id="public-sharing-toggle"
                checked={data.settings.publicSharingEnabled}
                disabled={publicSharingMutation.isPending || !isAdmin}
                onCheckedChange={(checked) => publicSharingMutation.mutate({ publicSharingEnabled: checked })}
                aria-labelledby="public-sharing-label"
              />
            </ActionTooltip>
          </div>
        </section>
      )}

      {/* Members + Invites. A sibling workspace (ACT2-1124) has them on Settings › Users instead. */}
      {isSiblingWorkspace === false && <SettingsMembers />}

      {/* ACT2-1124: the RudderStack workspaces that a sibling workspace can read (D18). Every member sees the list. */}
      {isSiblingWorkspace === true && <ReadableWorkspacesSection />}

      {/* Danger Zone */}
      {workspace && (
        <section className="mb-8">
          <h2 className="mb-4 text-sm font-bold tracking-wider text-danger uppercase">Danger Zone</h2>

          {isOwner ? (
            /* Delete workspace */
            <div className="flex items-center justify-between gap-4 overflow-hidden rounded-xl bg-danger-bg px-4 py-4">
              <div>
                <p className="text-sm font-medium text-grey-800">Delete workspace</p>
                <p className="mt-1.5 text-sm text-grey-500">
                  This permanently removes all dashboards, chat history, and settings.
                </p>
              </div>
              <Button
                variant="primary-destructive"
                className="shrink-0"
                onClick={() => setDeleteDialogOpen(true)}
                label="Delete"
              />
            </div>
          ) : (
            /* Leave workspace (non-owners) */
            <div className="flex items-center justify-between gap-4 overflow-hidden rounded-xl bg-danger-bg px-4 py-4">
              <div>
                <p className="text-sm font-medium text-grey-800">Leave workspace</p>
                <p className="mt-1.5 text-sm text-grey-500">You will lose access to this Lookout workspace.</p>
              </div>
              <Button
                variant="secondary-destructive"
                className="shrink-0"
                onClick={() => void handleLeave()}
                label="Leave"
              />
            </div>
          )}
        </section>
      )}

      {/* Delete workspace confirmation */}
      {workspace && isOwner && (
        <Dialog
          open={deleteDialogOpen}
          onOpenChange={(open) => {
            setDeleteDialogOpen(open);
            if (!open) setDeleteConfirm('');
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete this workspace?</DialogTitle>
              <DialogDescription>
                This permanently deletes <span className="font-medium text-foreground">{workspace.name}</span>, all
                connections, dashboards, and history. This cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-2">
              <Label htmlFor="delete-workspace-confirm">
                <span>
                  Type{' '}
                  <span className="rounded-sm bg-grey-50 px-1 py-0.5 font-mono font-medium text-danger">
                    {workspace.id}
                  </span>{' '}
                  to confirm
                </span>
              </Label>
              <Input
                id="delete-workspace-confirm"
                type="text"
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder={workspace.id}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setDeleteDialogOpen(false)} label="Cancel" />
              <Button
                type="button"
                variant="primary-destructive"
                onClick={handleDelete}
                disabled={deleteConfirm !== workspace.id}
                loading={deleteMutation.isPending}
                label="Delete workspace"
              />
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Read-only: what the workspace is connected to (set at workspace creation). A sibling workspace (ACT2-1124)
          shows its RudderStack organization: its own RudderStack workspace is hidden, so its id means nothing to a
          person. A shared-key workspace shows the RudderStack workspace it links. A member sees neither id. */}
      {data?.settings?.rudderstackLinked && (
        <div className="mt-2 mb-8 flex items-center justify-center gap-1.5 text-sm text-muted-foreground">
          {connectedId ? (
            <>
              <span>
                {connectedId.label}: <span className="font-medium text-foreground">{connectedId.value}</span>
              </span>
              <CopyButton
                text={connectedId.value}
                variant="tertiary"
                iconOnly
                copiedFeedback
                aria-label={`Copy ${connectedId.label.replace('Connected ', '')}`}
              />
            </>
          ) : (
            <span>Connected to RudderStack</span>
          )}
          {data.settings.rudderstackReadOnly && <Badge variant="info">Read-only</Badge>}
        </div>
      )}
    </>
  );
}
